/**
 * Admin User Management Service
 * Handles all API calls for admin/user role assignment and management
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Standard error handler for all admin API calls
const handleResponse = async (response) => {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error ?? err.message ?? `Request failed: ${response.status}`);
  }
  return response.json();
};

/**
 * List all admin and superadmin users (paginated, filterable)
 * GET /api/admin/users
 */
export const listAdmins = async (params, idToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const queryString = new URLSearchParams(params).toString();
  const url = `${API_URL}/api/admin/users${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return await handleResponse(response);
};

/**
 * Get single admin user details including status history
 * GET /api/admin/users/:userId
 */
export const getAdmin = async (userId, idToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'GET',
    headers,
  });

  return await handleResponse(response);
};

/**
 * Promote a user to admin or superadmin role
 * POST /api/admin/users/:userId/promote
 * 
 * @param {string} userId - User UID to promote
 * @param {object} data - { role, reason? }
 * @param {string} idToken - Firebase ID token
 */
export const promoteUser = async (userId, data, idToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const response = await fetch(`${API_URL}/api/admin/users/${userId}/promote`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  return await handleResponse(response);
};

/**
 * Demote an admin back to regular user role
 * POST /api/admin/users/:userId/demote
 * 
 * @param {string} userId - User UID to demote
 * @param {object} data - { reason? }
 * @param {string} idToken - Firebase ID token
 */
export const demoteUser = async (userId, data, idToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const response = await fetch(`${API_URL}/api/admin/users/${userId}/demote`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  return await handleResponse(response);
};

/**
 * Combined namespace export for consistency with adminService pattern
 */
export const adminUserAPI = {
  listAdmins,
  getAdmin,
  promoteUser,
  demoteUser,
};
